import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {AbortError, ApiError, CustomError, IncompleteResponseError, isUnavailableError, NetworkError, SilentError, TimeoutError, ViolationsError} from '../utils/ApiError'

import {useEvent} from './useEvent'
import {useNotification} from './useNotification'

type Hook = (err: Error) => void

export function useErrorHandler(): Hook {
  const {notifyError} = useNotification()
  const {t} = useTranslation()

  const notifications = useMemo(() => ({
    timeout: `${t('error.http.timeout.title')}
${t('error.http.timeout.body')}`,
    incomplete: `${t('error.http.incomplete.title')}
${t('error.http.incomplete.body')}`,
    unavailable: `${t('error.http.unavailable.title')}
${t('error.http.unavailable.body')}`,
    http400: t('error.http.400'),
    http401: t('error.http.401'),
    http402: t('error.http.402'),
    http403: t('error.http.403'),
    http404: t('error.http.404.title'),
    http422: t('error.http.422'),
    http500: t('error.http.500.title'),
    unexpected: `${t('error.http.unexpected.title')}
${t('error.http.unexpected.body')}`,
  }), [t])

  return useEvent((err: Error) => {
    if (err instanceof ViolationsError) {
      const errors: string[] = []
      for (const violation of err.violations) {
        const field = violation.propertyPath
        errors.push(field ? `${field}: ${violation.message}` : violation.message)
      }
      notifyError(`${notifications.http400}
${errors.map((error) => `• ${error}`).join('\n')}`)
    } else if (err instanceof NetworkError) {
      // do nothing
    } else if (err instanceof AbortError) {
      // do nothing: the request was superseded (navigation, logout, a newer keystroke), not failed
    } else if (err instanceof TimeoutError) {
      notifyError(notifications.timeout)
    } else if (err instanceof IncompleteResponseError) {
      notifyError(notifications.incomplete)
    } else if (err instanceof ApiError && [404].includes(err.statusCode)) {
      // do nothing, silent 404 errors (happens when deleting an item => conflict with mercure + mutation)
    } else if (isUnavailableError(err)) {
      // a gateway answered for a backend it could not reach: nothing the user did, and nothing to fix client-side
      notifyError(notifications.unavailable)
    } else if (err instanceof ApiError && [400, 401, 402, 403, 404, 422, 500].includes(err.statusCode)) {
      notifyError(notifications[`http${err.statusCode as 400 | 401 | 402 | 403 | 404 | 422 | 500}`])
    } else if (err instanceof ApiError && err.statusCode >= 500) {
      notifyError(notifications.http500)
    } else if (err instanceof ApiError) {
      notifyError(notifications.http400)
    } else if (err instanceof CustomError) {
      notifyError(err.message)
    } else if (err instanceof SilentError) {
      // do nothing
    } else {
      // not an API failure at all (a bug in the app, a broken lazy chunk, …): claiming a server error would
      // send the user chasing something they cannot act on
      notifyError(notifications.unexpected)
    }
  })
}
